"use client";

import { ReactNode, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { SessionProvider } from "next-auth/react";
import NextTopLoader from "nextjs-toploader";
import { Toaster } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import config from "@/config";
import { CreditsProvider } from "@/contexts/CreditsContext";
import { AssetsProvider } from "@/contexts/AssetsContext";
import { pageview, setUserId, restoreAndCleanupRedirectSession, storeSessionForRedirect } from "../libs/ga";
import { captureAttribution, track, trackFunnel, getOrCreateVisitorId } from "../libs/analytics";
import PromoBanner from "@/components/PromoBanner";
import { usePromoBannerHeight } from "@/hooks/usePromoBannerHeight";
import { CrossSellDevPreviewProvider } from "@/contexts/CrossSellDevPreviewContext";

// Separate component for GA user tracking
const GAUserTracking = (): null => {
  const { data } = useSession();

  // Set GA user_id when available to stitch sessions across domains
  useEffect(() => {
    if (data?.user?.id) {
      setUserId(data.user.id);

      // Only track authentication ONCE per session (not on every page load)
      const authTracked = sessionStorage.getItem('pfp_auth_tracked');
      if (!authTracked) {
        trackFunnel('acquisition', 'authenticated', {
          user_id: data.user.id,
          auth_method: 'google' // Could be enhanced to detect actual method
        });
        sessionStorage.setItem('pfp_auth_tracked', 'true');
      }
    }
  }, [data?.user?.id]);

  return null;
};

// All the client wrappers are here (they can't be in server components)
// 1. SessionProvider: Allow the useSession from next-auth (find out if user is auth or not)
// 2. NextTopLoader: Show a progress bar at the top when navigating between pages
// 3. Toaster: Show Success/Error messages anywhere from the app with toast()
// 4. Tooltip: Show tooltips if any JSX elements has these 2 attributes: data-tooltip-id="tooltip" data-tooltip-content=""
// 5. GAUserTracking: Handle Google Analytics user tracking and session restoration
const ClientLayout = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const sessionRestoredRef = useRef(false);
  usePromoBannerHeight();

  // Check for session restoration from OAuth/Stripe redirects ONCE on mount.
  // Also absorbs any lingering ?pfp_vid / ?pfp_sid from landing-page clickthrough.
  useEffect(() => {
    if (sessionRestoredRef.current) return;
    sessionRestoredRef.current = true;

    // Force evaluate visitorId so URL param / cookie bridge takes effect
    if (typeof window !== "undefined") {
      getOrCreateVisitorId();
    }

    const restored = restoreAndCleanupRedirectSession();
    if (restored && restored.redirectType) {
      const timeSinceRedirect = Date.now() - restored.timestamp;

      // Restore session ID if it was stored
      if (restored.sessionId && typeof window !== "undefined") {
        sessionStorage.setItem('pfp_session_id', restored.sessionId);
      }

      // Track successful session restoration after external redirect
      trackFunnel('acquisition', 'session_restored', {
        redirect_type: restored.redirectType,
        time_away: timeSinceRedirect,
        original_source: restored.source
      });
    }

  }, []);

  // Send page_view on route changes (skip admin + auth popup routes)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isInternal = pathname.startsWith('/admin_tools') || pathname.startsWith('/auth/');
    if (!isInternal) {
      captureAttribution();
    }
    pageview(window.location.pathname + window.location.search);
    if (!isInternal) {
      track('page_view', 'pageview', { page_path: window.location.pathname + window.location.search }, { skipGA: true });
    }
  }, [pathname]);

  // Hide decorative elements (banners, progress bars) in popup windows
  const isPopup = pathname.startsWith('/auth/popup');
  // Face AI funnel + admin tools — don't stack the sitewide promo
  const hidePromoBanner =
    isPopup ||
    pathname.startsWith('/face-ai') ||
    pathname.startsWith('/admin_tools');

  return (
    <>
      <SessionProvider>
        <CrossSellDevPreviewProvider>
        {/* Show a progress bar at the top when navigating between pages */}
        {!isPopup && <NextTopLoader color={config.colors.main} showSpinner={false} />}

        {/* Wrap everything with CreditsProvider and AssetsProvider to manage credits and assets globally */}
        <CreditsProvider>
          {/* Promo Sitewide Banner - hide in popups, Face AI, admin tools. Rendered inside
              CreditsProvider so it can read subscription status without a duplicate /user/me. */}
          <div data-promo-banner-slot>
            {!hidePromoBanner && <PromoBanner />}
          </div>

          <AssetsProvider>
          {/* Content inside app/page.js files  */}
          {children}
          </AssetsProvider>
        </CreditsProvider>

        {/* Show Success/Error messages anywhere from the app with toast() */}
        <Toaster
          toastOptions={{
            duration: 3000,
          }}
        />

        {/* Show tooltips if any JSX elements has these 2 attributes: data-tooltip-id="tooltip" data-tooltip-content="" */}
        <Tooltip
          id="tooltip"
          className="z-[60] !opacity-100 max-w-sm shadow-lg"
        />

        <GAUserTracking />
        </CrossSellDevPreviewProvider>
      </SessionProvider>
    </>
  );
};

export default ClientLayout;
